import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next'; // i18next import edilir
import TreeView, { TreeViewRef } from 'devextreme-react/tree-view';
import { AppFooter } from '../..';
import { getNavigation } from '../../../app-navigation';
import { useNavigation } from '../../../contexts/navigation';
import { useScreenSize } from '../../../utils/media-query';
import type { SideNavigationMenuProps } from '../../../types';
import './SideNavigationMenu.scss';
import * as events from 'devextreme/events';
import { appInfo } from '../../../app-info';

export type NavigationItem = {
  text: string;
  icon: string;
  path?: string;
  items?: Array<{
    text: string;
    path: string;
    icon?: string;
  }>;
};

export type SideNavigationItem = NavigationItem & {
  expanded: boolean;
};

export const SideNavigationMenu = (props: React.PropsWithChildren<SideNavigationMenuProps>) => {
  const {
    children,
    selectedItemChanged,
    openMenu,
    compactMode,
    onMenuReady,
    onOpenInNewTab,
    onOpenInSplitView,
  } = props;
  const { t } = useTranslation(); // useTranslation hook'u kullanılır

  const { isLarge } = useScreenSize();
  const navigationItems = useMemo(() => getNavigation(t) as NavigationItem[], [t]); // `t` değiştiğinde güncellenir

  const [contextMenu, setContextMenu] = useState<{ visible: boolean; x: number; y: number; path: string; title: string }>({
    visible: false,
    x: 0,
    y: 0,
    path: '',
    title: '',
  });


  function normalizePath() {
    return navigationItems.map((item) => ({
      ...item,
      expanded: isLarge,
      path: item.path && !/^\//.test(item.path) ? `/${item.path}` : item.path
    }));
  }

  const items: SideNavigationItem[] = useMemo(
    normalizePath,
    [navigationItems, isLarge]
  );

  const {
    navigationData: { currentPath },
  } = useNavigation();

  const treeViewRef = useRef<TreeViewRef>(null);
  const wrapperRef = useRef();
  const getWrapperRef = useCallback(
    (element) => {
      const prevElement = wrapperRef.current;
      if (prevElement) {
        events.off(prevElement, 'dxclick');
      }

      wrapperRef.current = element;
      events.on(element, 'dxclick', (e: React.PointerEvent) => {
        openMenu(e);
      });
    },
    [openMenu]
  );

  useEffect(() => {
    const treeView = treeViewRef.current && treeViewRef.current.instance();
    if (!treeView) {
      return;
    }
    if (currentPath !== undefined && currentPath !== "") {
      treeView.selectItem(currentPath);
      treeView.expandItem(currentPath);

    }

    if (compactMode) {
      treeView.collapseAll();
    }
  }, [currentPath, compactMode]);

  useEffect(() => {
    const treeView = treeViewRef.current && treeViewRef.current.instance();
    setTimeout(()=>{
      treeView?.collapseAll();
    },100)
  }, []);

  useEffect(() => {
    const closeMenu = () => {
      setContextMenu((prev) => (prev.visible ? { ...prev, visible: false } : prev));
    };
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, []); 

  return (
    <div className='dx-swatch-additional side-navigation-menu' ref={getWrapperRef}>
      {children}
      <div className='menu-container theme-dependent'>
        <TreeView
          ref={treeViewRef}
          items={items}
          keyExpr='path'
          selectionMode='single'
          focusStateEnabled={false}
          expandEvent='click'
          onItemClick={selectedItemChanged}
          onContentReady={onMenuReady}
          width='100%'
          onItemContextMenu={(e: any) => {
            const path = e?.itemData?.path;
            if (!path) {
              return;
            }
            e?.event?.preventDefault?.();
            setContextMenu({
              visible: true,
              x: e.event?.clientX ?? 0,
              y: e.event?.clientY ?? 0,
              path,
              title: e?.itemData?.text || '',
            });
          }}
        />
      </div>
      {contextMenu.visible && (
        <div
          className='side-nav-context-menu'
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          {onOpenInNewTab ? (
            <button
              type='button'
              onClick={() => {
                onOpenInNewTab?.(contextMenu.path, contextMenu.title);
                setContextMenu((prev) => ({ ...prev, visible: false }));
              }}
            >
              {t('openInNewTab')}
            </button>
          ) : null}
          {onOpenInSplitView ? (
            <button
              type='button'
              onClick={() => {
                onOpenInSplitView?.(contextMenu.path, contextMenu.title);
                setContextMenu((prev) => ({ ...prev, visible: false }));
              }}
            >
              {t('openInSplitView')}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
};